import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { fetchInvitePreview, type InvitePreview } from '../services/communityService';
import { useAuth } from '../contexts/AuthContext';

function previewStatusMessage(preview: InvitePreview): string | null {
  if (preview.invite_audience === 'admin') {
    return 'This invitation is for CWY Admin Portal staff only.';
  }
  switch (preview.invite_status) {
    case 'valid':
    case 'already_member':
      return null;
    case 'not_found':
      return 'This invitation link is not valid.';
    case 'expired':
      return 'This invitation link has expired. Ask a community admin for a new link.';
    case 'exhausted':
      return 'This invitation link has already been used. Ask a community admin for a new one.';
    case 'revoked':
      return 'This invitation link is no longer valid.';
    default:
      return 'This invitation link is not valid.';
  }
}

export const CommunityInviteLandingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleOpenInApp = () => {
    if (!token) return;
    window.location.href = `cwyapp://community/invite/${token}`;

    const timer = setTimeout(() => {
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isIOS) {
        window.location.href = 'https://apps.apple.com/app/id6752109391';
      } else {
        window.location.href = 'https://play.google.com/store/apps/details?id=com.cwy.app';
      }
    }, 2500);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearTimeout(timer);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange, { once: true });
  };

  useEffect(() => {
    if (!token) {
      setError('Invalid invite link.');
      setLoading(false);
      return;
    }

    if (isAuthenticated) {
      navigate('/chat', { replace: true, state: { openInviteToken: token } });
      return;
    }

    (async () => {
      try {
        const data = await fetchInvitePreview(token);
        setPreview(data);
      } catch {
        setError('Could not load invitation. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token, isAuthenticated, navigate]);

  if (isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress sx={{ color: '#0f9d58' }} />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress sx={{ color: '#0f9d58' }} />
      </Box>
    );
  }

  const statusMessage = preview ? previewStatusMessage(preview) : null;
  const inviteInvalid = Boolean(error || !preview || statusMessage);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        bgcolor: '#f5f5f5',
      }}
    >
      <Box
        sx={{
          p: 3,
          maxWidth: 380,
          width: '100%',
          textAlign: 'center',
          bgcolor: '#fff',
          borderRadius: 3,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}
      >
        {inviteInvalid ? (
          <>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Invalid invitation
            </Typography>
            <Alert severity="error" sx={{ mt: 2, textAlign: 'left', borderRadius: 2 }}>
              {error || statusMessage || 'This invitation link is not valid.'}
            </Alert>
          </>
        ) : isMobile ? (
          <>
            {preview!.community_avatar_url && (
              <Box
                component="img"
                src={preview!.community_avatar_url}
                alt=""
                sx={{ width: 64, height: 64, borderRadius: '50%', mb: 2, objectFit: 'cover' }}
              />
            )}
            <Typography variant="body1" color="text.secondary" gutterBottom>
              You&apos;ve been invited to join
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {preview!.community_name}
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={handleOpenInApp}
              sx={{
                borderRadius: 2,
                py: 1.1,
                bgcolor: '#0f9d58',
                '&:hover': { bgcolor: '#0b8043' },
                mb: 1.5,
                textTransform: 'none',
              }}
            >
              Open in App
            </Button>
            <Button
              component={RouterLink}
              to={`/login?redirect=${encodeURIComponent(`/community/invite/${token}`)}`}
              variant="outlined"
              fullWidth
              sx={{ borderRadius: 2, py: 1.1, mb: 3, textTransform: 'none' }}
            >
              Continue on Web
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Typography
                component="a"
                href="https://apps.apple.com/app/id6752109391"
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                App Store
              </Typography>
              <Typography
                component="a"
                href="https://play.google.com/store/apps/details?id=com.cwy.app"
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Google Play
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {preview!.community_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Sign in to join this community on Canadawale Yar.
            </Typography>
            <Button
              component={RouterLink}
              to={`/login?redirect=${encodeURIComponent(`/community/invite/${token}`)}`}
              variant="contained"
              fullWidth
              sx={{ borderRadius: 2, py: 1.1, bgcolor: '#0f9d58', '&:hover': { bgcolor: '#0b8043' } }}
            >
              Sign in to join
            </Button>
            <Button
              component={RouterLink}
              to={`/signup?redirect=${encodeURIComponent(`/community/invite/${token}`)}`}
              fullWidth
              sx={{ mt: 1, borderRadius: 2, textTransform: 'none' }}
            >
              Create account
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};
